import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  PDFViewer,
} from "@react-pdf/renderer";
import { useLocation } from "react-router-dom";
import fontBold from "../../pdf/fonts/NotoSansTamil-Bold.ttf";
import fontRegular from "../../pdf/fonts/NotoSansTamil-Regular.ttf";

// Register Tamil Fonts
Font.register({
  family: "TamilFont",
  fonts: [
    { src: fontRegular, fontWeight: "normal" },
    { src: fontBold, fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "TamilFont",
    fontSize: 12,
  },
  container: {
    border: "2pt solid blue",
    padding: 15,
    height: "100%",
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
  },
  headerBox: {
    border: "2pt solid black",
    borderRadius: 8,
    padding: 10,
    marginBottom: 10,
    alignItems: "center",
    width: "80%",
    alignSelf: "center",
  },
  schoolName: {
    fontSize: 22,
    fontWeight: "bold",
    color: "navy",
    marginBottom: 2,
  },
  auditoriumName: {
    fontSize: 13,
    fontWeight: "bold",
    color: "navy",
    marginBottom: 5,
    marginTop: 5,
  },
  address: {
    fontSize: 9,
    textAlign: "center",
    marginBottom: 2,
  },
  noDateRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginVertical: 10,
    paddingHorizontal: 10,
  },
  receiptLabel: {
    backgroundColor: "navy",
    color: "white",
    paddingVertical: 4,
    paddingHorizontal: 15,
    fontWeight: "bold",
    fontSize: 12,
    borderRadius: 2,
  },
  contentBody: {
    marginTop: 5,
    paddingHorizontal: 10,
  },
  row: {
    flexDirection: "row",
    marginBottom: 4,
    paddingVertical: 6,
    alignItems: "flex-end",
  },
  label: {
    fontSize: 14,
    fontWeight: "bold",
  },
  value: {
    flex: 1,
    marginLeft: 5,
    fontSize: 14,
    borderBottom: "1pt solid black",
    paddingLeft: 4,
    paddingBottom: 2,
  },
  footer: {
    marginTop: 40, // Content-க்கும் இதற்கும் இடையே உள்ள கேப்
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingHorizontal: 10,
    paddingBottom: 5,
  },
  amountBox: {
    flexDirection: "row",
    alignItems: "center",
    width: 180,
    // Content-ன் இடது பக்கத்தில் இருந்து துவங்க
  },
  amountValue: {
    fontSize: 16,
    borderBottom: "1pt solid black",
    flex: 1,
    marginLeft: 5,
    paddingBottom: 2,
    fontWeight: "bold",
  },
  signature: {
    textAlign: "center",
    width: 120,
    fontWeight: "bold",
    fontSize: 13,
    // கோடு நீக்கப்பட்டது
  },
});

const numberToWords = (num) => {
  if (!num) return "";
  const a = [
    "",
    "One ",
    "Two ",
    "Three ",
    "Four ",
    "Five ",
    "Six ",
    "Seven ",
    "Eight ",
    "Nine ",
    "Ten ",
    "Eleven ",
    "Twelve ",
    "Thirteen ",
    "Fourteen ",
    "Fifteen ",
    "Sixteen ",
    "Seventeen ",
    "Eighteen ",
    "Nineteen ",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const n = ("000000000" + num)
    .substr(-9)
    .match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return "";

  let str = "";
  str +=
    n[1] !== "00"
      ? (a[Number(n[1])] || b[n[1][0]] + " " + a[n[1][1]]) + "Crore "
      : "";
  str +=
    n[2] !== "00"
      ? (a[Number(n[2])] || b[n[2][0]] + " " + a[n[2][1]]) + "Lakh "
      : "";
  str +=
    n[3] !== "00"
      ? (a[Number(n[3])] || b[n[3][0]] + " " + a[n[3][1]]) + "Thousand "
      : "";
  str +=
    n[4] !== "0"
      ? (a[Number(n[4])] || b[n[4][0]] + " " + a[n[4][1]]) + "Hundred "
      : "";
  str +=
    n[5] !== "00"
      ? (str !== "" ? "and " : "") +
        (a[Number(n[5])] || b[n[5][0]] + " " + a[n[5][1]])
      : "";

  return str.trim() + " only /-";
};

const DanceClassBillPDF = ({ data }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formattedDate = formatDate(data?.dance_class_bill_date);
  const amountWords = numberToWords(parseInt(data?.amount || 0));

  return (
    <Document>
      <Page size="A5" orientation="landscape" style={styles.page}>
        <View style={styles.container}>
          {/* Header Box */}
          <View style={styles.headerBox}>
            <Text style={styles.schoolName}>நிதிக் நிருத்யாலயா</Text>
            <Text style={styles.auditoriumName}>கலைக்கூடம்</Text>
            <Text style={styles.address}>
              No : 1-23-42/10 , ஸ்ரீ வித்யா காம்பிளக்ஸ் கனகவேல் நகர் ,
            </Text>
            <Text style={styles.address}>
              யூனியன் ஆபீஸ் ஸ்டாப் அருப்புக்கோட்டை - 6261011
            </Text>
          </View>

          {/* Bill No and Date Row */}
          <View style={styles.noDateRow}>
            <View style={{ width: "30%" }}>
              <Text>
                <Text style={{ fontWeight: "bold" }}>No. </Text>
                {data?.dance_class_bill_no}
              </Text>
            </View>
            <View style={{ width: "40%", alignItems: "center" }}>
              <Text style={styles.receiptLabel}>பற்றுச்சீட்டு</Text>
            </View>
            <View
              style={{
                width: "30%",
                flexDirection: "row",
                justifyContent: "flex-end",
              }}
            >
              <Text style={{ fontWeight: "bold" }}>Date : </Text>
              <Text
                style={{
                  borderBottom: "1pt solid black",
                  minWidth: 70,
                  textAlign: "center",
                }}
              >
                {formattedDate}
              </Text>
            </View>
          </View>

          {/* Main Content */}
          <View style={styles.contentBody}>
            <View style={styles.row}>
              <Text style={styles.label}>Group :</Text>
              <Text style={styles.value}>{data?.class_name}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>
                Received with thanks from Sri. / Smt. :
              </Text>
              <Text style={styles.value}>{data?.parent_name}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>Child Name :</Text>
              <Text style={styles.value}>{data?.child_name}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>the sum of Rs. :</Text>
              <Text style={styles.value}>{amountWords}</Text>
            </View>

            <View style={styles.row}>
              <Text style={styles.label}>towards :</Text>
              <Text style={styles.value}>{data?.towards}</Text>
            </View>
          </View>

          {/* Footer - Amount and Signature */}
          <View style={styles.footer}>
            <View style={styles.amountBox}>
              <Text style={{ fontWeight: "bold", fontSize: 16 }}>Rs. </Text>
              <Text style={styles.amountValue}>{data?.amount}/-</Text>
            </View>
            <View>
              <Text style={styles.signature}>Signature</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const DanceClassBillPDFView = () => {
  const location = useLocation();
  const { rowData } = location.state || {};

  return (
    <div style={{ height: "100vh", width: "100%" }}>
      <PDFViewer style={{ width: "100%", height: "100%" }}>
        <DanceClassBillPDF data={rowData} />
      </PDFViewer>
    </div>
  );
};

export default DanceClassBillPDF;
